import { useEffect } from "react";
import "./who-its-for.css";

const PAGE_TITLE = "Who Apping Sales OS Is For | B2B Market Expansion & Sales";
const PAGE_DESCRIPTION = "See which B2B teams Apping Sales OS is designed for, including manufacturers, export sales teams and businesses entering unfamiliar markets.";

const navigation = [
  ["Product", "/product"],
  ["Market Entry", "/market-entry"],
  ["Who It’s For", "/who-its-for"],
  ["Growth", "/growth"],
  ["Insights", "/insights"],
  ["About", "/about"],
];

const problemQuestions = [
  "Which companies actually matter?",
  "Why are they relevant?",
  "Who inside the business should we engage?",
  "What have we already learned?",
  "What should we do next?",
];

const manufacturerSituations = [
  "Entering a new country",
  "Identifying potential industrial buyers",
  "Exploring new customer segments",
  "Expanding distributor or channel coverage",
  "Building an export-sales territory",
];

const stakeholderRoles = ["Senior management", "Procurement", "Operations", "Technical teams", "Commercial teams", "Finance"];

const profiles = [
  {
    type: "Industrial equipment manufacturer",
    objective: "Enter Malaysia and identify potential manufacturers that may have a relevant equipment requirement.",
    need: "Market context, company identification, stakeholder discovery and structured follow-up.",
  },
  {
    type: "Export-oriented B2B supplier",
    objective: "Develop a new Southeast Asian territory.",
    need: "Understand where relevant accounts are concentrated and who may influence purchasing decisions.",
  },
  {
    type: "Regional technology company",
    objective: "Enter a new vertical in an unfamiliar market.",
    need: "Identify relevant businesses, map account stakeholders and preserve learning as outreach develops.",
  },
  {
    type: "Relationship-led professional B2B business",
    objective: "Develop a focused list of strategic accounts rather than mass outreach.",
    need: "Build account context over time and coordinate next actions.",
  },
];

const lowerComplexity = ["Known customers", "Short sales cycle", "Single decision-maker", "Simple transaction"];
const higherComplexity = ["New market", "Many possible accounts", "Multiple stakeholders", "Longer sales cycle", "Relationship context", "Repeated follow-up"];
const notFit = [
  "The sale is highly transactional",
  "Customers purchase immediately with little research",
  "There are very few accounts to manage",
  "Account context has little influence on the sale",
  "The main objective is sending the highest possible volume of messages",
  "The business primarily needs a basic contact database",
];

const workflow = [
  "Where should we grow?",
  "Which companies matter?",
  "Who matters inside them?",
  "What happened when we engaged?",
  "What have we learned?",
  "What should we do next?",
];

const fitQuestions = [
  "We want to enter a market we do not yet understand deeply.",
  "We have too many possible companies and need to know where to focus.",
  "One contact rarely represents the entire buying decision.",
  "Our sales team spends too much time rebuilding research and context.",
  "Useful account knowledge is scattered between systems and people.",
  "We want outreach to become part of a longer intelligence loop.",
];

function Header() {
  return (
    <header className="fit-header">
      <a className="fit-brand" href="/" aria-label="Apping Sales OS homepage"><img src="/apping-logo.png" alt="Apping Technology" width="985" height="999" /></a>
      <nav aria-label="Primary navigation">{navigation.map(([label, href]) => <a className={label === "Who It’s For" ? "is-active" : ""} key={label} href={href}>{label}</a>)}</nav>
      <div className="fit-header-actions"><button type="button" aria-label="Language selection. English is active" aria-disabled="true">EN <span aria-hidden="true">| 简</span></button><a className="fit-button fit-button-small" href="/book-a-walkthrough">Book a walkthrough</a></div>
    </header>
  );
}

function Intro({ eyebrow, title, children, className = "" }) {
  return <div className={`fit-intro ${className}`.trim()}>{eyebrow ? <p className="fit-eyebrow">{eyebrow}</p> : null}<h2>{title}</h2>{children ? <div className="fit-copy">{children}</div> : null}</div>;
}

function Footer() {
  return (
    <footer className="fit-footer">
      <a className="fit-footer-brand" href="/">Apping Sales OS</a>
<nav aria-label="Footer navigation"><a href="/product">Product</a><a href="/market-entry">Market Entry</a><a href="/sales-intelligence">Sales Intelligence</a><a href="/who-its-for">Who It’s For</a><a href="/growth">Apping Growth</a><a href="/insights">Insights</a><a href="/about">About</a><a href="/faq">FAQ</a><a href="/book-a-walkthrough">Book a Walkthrough</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/business-contact-data">Business Contact Data</a><a href="/privacy-request">Privacy Request</a></nav>
      <p>Apping Technology</p>
    </footer>
  );
}

export default function WhoItsForPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content") || "";
    document.title = PAGE_TITLE;
    description?.setAttribute("content", PAGE_DESCRIPTION);
    document.documentElement.dataset.page = "who-its-for";
    window.__APPING_WHO_ITS_FOR_READY__ = true;
    return () => {
      document.title = previousTitle;
      description?.setAttribute("content", previousDescription);
      delete document.documentElement.dataset.page;
      delete window.__APPING_WHO_ITS_FOR_READY__;
    };
  }, []);

  return (
    <div className="fit-page">
      <a className="fit-skip" href="#fit-main">Skip to content</a>
      <Header />
      <main id="fit-main">
        <section className="fit-hero" aria-labelledby="fit-hero-title">
          <img className="fit-hero-image" src="/who-its-for-hero.png" alt="A two-dimensional regional market map connecting to a focused company and its stakeholders" width="1536" height="1024" fetchPriority="high" />
          <div className="fit-hero-shade" aria-hidden="true" />
          <div className="fit-container fit-hero-inner">
            <div className="fit-hero-copy">
              <p className="fit-eyebrow">Who it’s for</p>
              <h1 id="fit-hero-title">Built for B2B sales where context matters.</h1>
              <p>Apping Sales OS is designed for teams that need to understand markets, identify the right companies, reach relevant stakeholders and build useful sales intelligence as opportunities develop.</p>
              <div className="fit-actions"><a className="fit-button" href="/book-a-walkthrough">Book a walkthrough</a><a className="fit-text-link" href="/market-entry">Explore Market Entry</a></div>
            </div>
          </div>
        </section>

        <aside className="fit-support-line">Especially relevant when the sale is complex, international or relationship-led.</aside>

        <section id="common-problem" className="fit-section problem-section" aria-labelledby="problem-title">
          <div className="fit-container problem-layout">
            <Intro title="The problem is rarely finding more names."><p>For many B2B teams, there are already more companies, contacts and information sources than the sales team can reasonably work through.</p></Intro>
            <ol className="question-stack">{problemQuestions.map((question, index) => <li key={question}><span>{String(index + 1).padStart(2, "0")}</span>{question}</li>)}</ol>
            <p className="fit-statement">Sales OS is built for teams that need better context, not simply more volume.</p>
          </div>
        </section>

        <section id="manufacturers" className="fit-section manufacturers-section" aria-labelledby="manufacturers-title">
          <div className="fit-container manufacturers-layout">
            <Intro eyebrow="Manufacturers" title="Find potential buyers beyond your home market."><p>Manufacturers expanding internationally often need to understand an unfamiliar market before their sales team knows which businesses deserve attention.</p></Intro>
            <figure className="fit-environment fit-environment-manufacturing"><img src="/images/editorial/industrial-production.webp" srcSet="/images/editorial/industrial-production-800.webp 800w, /images/editorial/industrial-production.webp 1600w" sizes="(max-width: 760px) 100vw, 48vw" alt="Industrial machinery operating across a modern manufacturing floor" width="1600" height="1067" loading="lazy" decoding="async" /><figcaption>Illustrative manufacturing environment. No customer relationship is implied.</figcaption></figure>
            <div className="fit-market-path" role="img" aria-label="Market leads to relevant companies, stakeholders and sales action"><span>Market</span><b aria-hidden="true">→</b><span>Relevant companies</span><b aria-hidden="true">→</b><span>Stakeholders</span><b aria-hidden="true">→</b><span>Sales action</span></div>
            <div className="situation-field">{manufacturerSituations.map((item) => <span key={item}>{item}</span>)}</div>
          </div>
        </section>

        <section id="export-sales" className="fit-section export-section" aria-labelledby="export-title">
          <div className="fit-container export-layout">
            <div className="territory-visual" aria-hidden="true"><span>Unfamiliar territory</span><i /><strong>Focused accounts</strong></div>
            <figure className="fit-environment fit-environment-export"><img src="/images/editorial/cross-border-logistics-port.webp" srcSet="/images/editorial/cross-border-logistics-port-800.webp 800w, /images/editorial/cross-border-logistics-port.webp 1400w" sizes="(max-width: 760px) 100vw, 48vw" alt="Container port handling international cargo and shipping operations" width="1400" height="788" loading="lazy" decoding="async" /><figcaption>Illustrative international logistics environment.</figcaption></figure>
            <Intro eyebrow="Export sales" title="Give your team a clearer territory to work."><p>An international sales team may be responsible for a country or region it does not yet understand deeply.</p><p>Sales OS helps organize market and account context around the commercial objective so the team can focus its effort more deliberately.</p><blockquote>Less time rebuilding the territory. More time understanding the accounts within it.</blockquote></Intro>
          </div>
        </section>

        <section id="business-development" className="fit-section development-section" aria-labelledby="development-title">
          <div className="fit-container">
            <Intro eyebrow="Business development" title="Turn a broad growth mandate into a focused starting point."><p>Regional business-development teams are often given a clear objective while the path to the right accounts remains unclear. Sales OS helps bridge that gap.</p></Intro>
            <figure className="fit-environment fit-environment-region"><img src="/images/editorial/southeast-asia-business-district.webp" srcSet="/images/editorial/southeast-asia-business-district-800.webp 800w, /images/editorial/southeast-asia-business-district.webp 1600w" sizes="(max-width: 760px) 100vw, 58vw" alt="Singapore central business district across Marina Bay at dusk" width="1600" height="1067" loading="lazy" decoding="async" /><figcaption>Illustrative Southeast Asian commercial environment.</figcaption></figure>
            <div className="mandate-grid" aria-label="Examples of regional growth mandates"><blockquote>“Grow Southeast Asia.”</blockquote><blockquote>“Enter Malaysia.”</blockquote><blockquote>“Find buyers in Indonesia.”</blockquote><blockquote>“Develop new accounts in Hong Kong.”</blockquote></div>
          </div>
        </section>

        <section id="complex-sales" className="fit-section complex-section" aria-labelledby="complex-title">
          <div className="fit-container complex-layout">
            <Intro eyebrow="Complex sales" title="When one contact is not the whole buying process."><p>Many B2B purchases involve several people with different interests and responsibilities.</p><p>Sales OS helps teams approach an account as a business context, not simply a contact record.</p><p className="fit-statement-inline">A company is not a lead. A contact is not the whole account.</p></Intro>
            <figure className="fit-environment fit-environment-complex"><img src="/images/editorial/complex-b2b-meeting.webp" srcSet="/images/editorial/complex-b2b-meeting-800.webp 800w, /images/editorial/complex-b2b-meeting.webp 1600w" sizes="(max-width: 760px) 100vw, 48vw" alt="Business team listening during a meeting around a conference table" width="1600" height="1067" loading="lazy" decoding="async" /><figcaption>Illustrative complex B2B decision environment. The people pictured are not represented as Apping customers.</figcaption></figure>
            <div className="stakeholder-map" aria-label="Relevant roles surrounding one business account"><strong>Business account</strong>{stakeholderRoles.map((role) => <span key={role}>{role}</span>)}</div>
          </div>
        </section>

        <section id="relationship-led" className="fit-section relationship-section" aria-labelledby="relationship-title">
          <div className="fit-container">
            <Intro eyebrow="Relationship-led sales" title="Useful context becomes more valuable over time."><p>In relationship-led B2B sales, opportunities can develop over weeks, months or longer. Sales OS is designed to help useful account context carry forward rather than restart every time.</p></Intro>
            <ol className="relationship-timeline"><li><span>First conversation</span><p>No immediate opportunity</p></li><li><span>New stakeholder</span><p>Account context changes</p></li><li><span>Later requirement</span><p>Previous learning matters</p></li><li><span>Renewed relevance</span><p>The account does not restart</p></li></ol>
          </div>
        </section>

        <section id="southeast-asia" className="fit-section sea-section" aria-labelledby="sea-title">
          <div className="fit-container sea-layout">
            <Intro eyebrow="Southeast Asia" title="Entering Southeast Asia is rarely one market-entry problem."><p>Malaysia, Singapore, Indonesia, Thailand, Vietnam, the Philippines and other Southeast Asian markets differ in company structure, commercial environment, industry landscape and relationship dynamics.</p><p>Sales OS is designed to help teams explore each market around the actual commercial objective rather than treating the region as one homogeneous territory.</p></Intro>
            <div className="sea-market-field" aria-label="Southeast Asian markets considered as distinct commercial environments"><span>Malaysia</span><span>Singapore</span><span>Indonesia</span><span>Thailand</span><span>Vietnam</span><span>Philippines</span><strong>One region. Distinct market contexts.</strong></div>
          </div>
        </section>

        <section id="illustrative-profiles" className="fit-section profiles-section" aria-labelledby="profiles-title">
          <div className="fit-container">
            <Intro title="What a Sales OS customer might look like."><p className="profile-disclosure">Illustrative customer profiles. These examples do not represent named customers or claimed outcomes.</p></Intro>
            <div className="profile-grid">{profiles.map((profile, index) => <article key={profile.type}><span>Profile {String.fromCharCode(65 + index)}</span><h3>{profile.type}</h3><dl><div><dt>Objective</dt><dd>{profile.objective}</dd></div><div><dt>Need</dt><dd>{profile.need}</dd></div></dl></article>)}</div>
          </div>
        </section>

        <section id="complexity-fit" className="fit-section complexity-section" aria-labelledby="complexity-title">
          <div className="fit-container">
            <Intro title="Sales OS becomes more useful as the sales problem becomes less simple." />
            <div className="complexity-flow"><article><span>Lower complexity</span><div>{lowerComplexity.map((item) => <p key={item}>{item}</p>)}</div><small>Sales OS may provide less incremental value here.</small></article><b aria-hidden="true">→</b><article><span>Higher complexity</span><div>{higherComplexity.map((item) => <p key={item}>{item}</p>)}</div><small>This is where Sales OS becomes more relevant.</small></article></div>
          </div>
        </section>

        <section id="not-right-fit" className="fit-section not-fit-section" aria-labelledby="not-fit-title">
          <div className="fit-container not-fit-layout">
            <Intro eyebrow="Fit matters" title="Sales OS is not designed for every sales model."><p>It may be less relevant when:</p></Intro>
            <div className="not-fit-list">{notFit.map((item) => <p key={item}>{item}</p>)}</div>
            <p className="fit-statement">Sales OS is built for sales environments where understanding the account matters before, during and after outreach.</p>
          </div>
        </section>

        <section id="continuous-workflow" className="fit-section workflow-section" aria-labelledby="workflow-title">
          <div className="fit-container workflow-layout">
            <Intro eyebrow="One continuous workflow" title="The best fit is where market entry and account intelligence meet."><p>That is the problem Sales OS is designed around.</p><div className="workflow-links"><a className="fit-text-link" href="/market-entry">Explore Market Entry</a><a className="fit-text-link" href="/sales-intelligence">Explore Sales Intelligence</a></div></Intro>
            <ol className="workflow-sequence">{workflow.map((item, index) => <li key={item}><span>{item}</span>{index < workflow.length - 1 ? <b aria-hidden="true">↓</b> : null}</li>)}</ol>
          </div>
        </section>

        <section id="conceptual-preview" className="fit-section compact-preview-section" aria-labelledby="compact-preview-title">
          <div className="fit-container compact-preview">
            <div><p className="fit-eyebrow">Conceptual product preview</p><h2 id="compact-preview-title">A concise view of the context path.</h2><p>This illustrative flow shows the relationship between a market, a focused account, relevant stakeholders and the context carried forward.</p></div>
            <ol aria-label="Illustrative product flow"><li><span>Market</span><small>Commercial objective</small></li><li><span>Account</span><small>Focused company</small></li><li><span>Stakeholders</span><small>Relevant roles</small></li><li><span>Context</span><small>Available for the next decision</small></li></ol>
            <p className="compact-preview-note">Conceptual preview. This is not a live product recording.</p>
          </div>
        </section>

        <section id="fit-test" className="fit-section fit-test-section" aria-labelledby="fit-test-title">
          <div className="fit-container">
            <Intro eyebrow="A simple test" title="Sales OS may be relevant if these questions sound familiar." />
            <div className="fit-checks">{fitQuestions.map((question) => <p key={question}>{question}</p>)}</div>
            <p className="fit-test-close">If several of these describe your sales environment, Sales OS may be worth exploring.</p>
          </div>
        </section>

        <section id="fit-walkthrough" className="fit-section fit-walkthrough-section" aria-labelledby="fit-walkthrough-title">
          <div className="fit-container fit-walkthrough-inner">
            <p className="fit-eyebrow">Apply Sales OS to your business</p>
            <h2 id="fit-walkthrough-title">Start with the market or sales problem you are trying to solve.</h2>
            <p>Tell us what you sell, where you want to grow and the type of customers your team is trying to reach. We’ll use the walkthrough to explore whether Sales OS fits the way your business sells.</p>
            <div className="fit-actions"><a className="fit-button" href="/book-a-walkthrough">Book a walkthrough</a><a className="fit-text-link" href="/product">Explore Sales OS</a></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
